import {
  AUDIENCE_TYPE_OPTIONS,
  DELIVERY_MODE_PACK_FIELDS,
  DeliveryModeValue,
} from "../../Config/Expert";
import { ExpertFormSectionCard } from "./ExpertFormSectionCard";

type PackKey = "three_session" | "five_session" | "ten_session";

type ExpertSessionPricingFormProps = {
    audienceConfig: any;
    onAudienceChange: (next: any) => void;
    enabled: Record<DeliveryModeValue, boolean>;
    onEnabledChange: (mode: DeliveryModeValue, value: boolean) => void;
    selectedTypes?: string[];
};

export const ExpertSessionPricingForm = ({
    audienceConfig,
    onAudienceChange,
    enabled,
    onEnabledChange,
    selectedTypes = [],
}: ExpertSessionPricingFormProps) => {
    const typeSet = new Set(
        (selectedTypes || []).map((t) => String(t).toLowerCase())
    );
    const showAudience = (audience: string) => {
        if (!selectedTypes || selectedTypes.length === 0) return true;
        if (audience === "personal") {
            return typeSet.has("personal") || typeSet.has("individual");
        }
        return typeSet.has(audience);
    };

    const handleAudienceFieldChange = (
        audience: string,
        field: "session_price" | "duration",
        mode: string,
        fieldValue: string
    ) => {
        onAudienceChange({
            ...audienceConfig,
            [audience]: {
                ...audienceConfig[audience],
                [field]: {
                    ...audienceConfig[audience]?.[field],
                    [mode]: fieldValue,
                },
            },
        });
    };

    const handlePackChange = (
        audience: string,
        mode: string,
        packKey: PackKey,
        fieldValue: string
    ) => {
        onAudienceChange({
            ...audienceConfig,
            [audience]: {
                ...audienceConfig[audience],
                subscription_price: {
                    ...audienceConfig[audience]?.subscription_price,
                    [mode]: {
                        ...audienceConfig[audience]?.subscription_price?.[mode],
                        [packKey]: fieldValue,
                    },
                },
            },
        });
    };

    const visibleAudiences = AUDIENCE_TYPE_OPTIONS.filter((opt) =>
        showAudience(opt.value)
    );

    return (
        <div className="form-group col-md-12 mb-4">
            <h5 className="mb-3">Session Price &amp; Duration</h5>
            <p className="text-muted mb-3">
                1 / 3 / 5 / 10 session prices and duration are per audience type for
                each delivery mode.
            </p>
            <p
                className="mb-3"
                style={{ color: "#308888", fontWeight: 600 }}
            >
                Note: If any of the 3, 5, or 10 Session Price fields are filled for an
                audience, the 1 Session Price becomes mandatory for that audience and
                delivery mode. If all of 3, 5, and 10 Session Price are left blank, 1
                Session Price is not mandatory.
            </p>
            {DELIVERY_MODE_PACK_FIELDS.map((mode) => (
                <ExpertFormSectionCard
                    key={mode.value}
                    title={mode.label}
                    extra={
                        <label className="mb-0 d-flex align-items-center">
                            <span className="mr-2">Is Active</span>
                            <input
                                type="checkbox"
                                aria-label={`${mode.label} active`}
                                checked={Boolean(enabled[mode.value])}
                                onChange={(e) => onEnabledChange(mode.value, e.target.checked)}
                                id={mode.enabledKey}
                                name={mode.enabledKey}
                            />
                        </label>
                    }
                >
                    <div className="col-12 mt-3 mb-1">
                        <div className="table-responsive">
                            <table className="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: "140px" }}>Audience</th>
                                        <th>1 Session Price</th>
                                        <th>3 Session Price</th>
                                        <th>5 Session Price</th>
                                        <th>10 Session Price</th>
                                        <th>Duration (min)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleAudiences.map((audience) => (
                                        <tr key={`${audience.value}-${mode.value}`}>
                                            <td className="align-middle">{audience.label}</td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`${audience.value}_${mode.value}_price`}
                                                    value={
                                                        audienceConfig?.[audience.value]?.session_price?.[
                                                        mode.value
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAudienceFieldChange(
                                                            audience.value,
                                                            "session_price",
                                                            mode.value,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`${audience.value}_${mode.value}_three`}
                                                    value={
                                                        audienceConfig?.[audience.value]
                                                            ?.subscription_price?.[mode.value]
                                                            ?.three_session || ""
                                                    }
                                                    onChange={(e) =>
                                                        handlePackChange(
                                                            audience.value,
                                                            mode.value,
                                                            "three_session",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`${audience.value}_${mode.value}_five`}
                                                    value={
                                                        audienceConfig?.[audience.value]
                                                            ?.subscription_price?.[mode.value]
                                                            ?.five_session || ""
                                                    }
                                                    onChange={(e) =>
                                                        handlePackChange(
                                                            audience.value,
                                                            mode.value,
                                                            "five_session",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`${audience.value}_${mode.value}_ten`}
                                                    value={
                                                        audienceConfig?.[audience.value]
                                                            ?.subscription_price?.[mode.value]
                                                            ?.ten_session || ""
                                                    }
                                                    onChange={(e) =>
                                                        handlePackChange(
                                                            audience.value,
                                                            mode.value,
                                                            "ten_session",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`${audience.value}_${mode.value}_duration`}
                                                    value={
                                                        audienceConfig?.[audience.value]?.duration?.[
                                                        mode.value
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAudienceFieldChange(
                                                            audience.value,
                                                            "duration",
                                                            mode.value,
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </ExpertFormSectionCard>
            ))}
        </div>
    );
};